<template>
  <div class="badges">

    <header class="masthead">
      <div>
        <h1 class="title">Badges<span class="dot">.</span></h1>
        <p class="sub">
          Type each person's badge ID beside their name. A person can have more
          than one card; a tap on any of them counts for that person. Until a
          badge is mapped, the person carrying it never shows as being in the
          building.
        </p>
      </div>

      <div class="counts" v-if="!loading && !error">
        <span><b>{{ mappedCount }}</b> mapped</span>
        <span><b>{{ people.length - mappedCount }}</b> to go</span>
      </div>
    </header>

    <div class="controls" v-if="!loading && !error">
      <input
        v-model="query"
        class="search"
        type="search"
        placeholder="Search name or email..."
        aria-label="Search"
      >

      <label class="only-blank">
        <input type="checkbox" v-model="onlyUnmapped">
        Show only unmapped
      </label>
    </div>

    <p v-if="loading" class="msg">Reading people</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="visible.length === 0" class="msg">Nobody matches</p>

    <table v-else class="table">
      <thead>
        <tr>
          <th class="col-name">Name</th>
          <th class="col-email">Email</th>
          <th class="col-badge">Badge IDs</th>
          <th class="col-state"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in visible" :key="p.id">
          <td class="name">{{ p.name }}</td>
          <td class="email">{{ p.email || '\u2014' }}</td>

          <td>
            <div class="badge-cell">
              <!-- Every card the person has, each removable on its own. -->
              <span
                v-for="uuid in (savedByPerson[p.id] ?? [])"
                :key="uuid"
                class="chip"
              >
                <span class="chip-id">{{ uuid }}</span>
                <button
                  class="chip-x"
                  type="button"
                  :aria-label="'Remove badge ' + uuid"
                  :title="'Remove ' + uuid"
                  @click="remove(p, uuid)"
                >&times;</button>
              </span>

              <!-- Saved when the field loses focus or on Enter, rather than
                   behind a button per row. Tab moves to the next person, so a
                   whole department can be typed without touching the mouse.
                   The field is for adding; existing cards are the chips. -->
              <input
                class="badge-input"
                type="text"
                :placeholder="(savedByPerson[p.id] ?? []).length ? 'Add another' : '—'"
                :value="draft[p.id] ?? ''"
                @input="draft[p.id] = $event.target.value"
                @blur="commit(p)"
                @keyup.enter="$event.target.blur()"
              >
            </div>
          </td>

          <td class="state">
            <span v-if="state[p.id] === 'saving'" class="pending">saving</span>
            <span v-else-if="state[p.id] === 'saved'" class="ok">saved</span>
            <span v-else-if="state[p.id]" class="bad">{{ state[p.id] }}</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mappings whose person is no longer in the directory. They cannot appear
         in the list above, so without this they would be invisible and
         impossible to remove. -->
    <section v-if="orphans.length > 0" class="orphans">
      <h2 class="section-title">Mapped to someone no longer in the directory</h2>
      <ul class="orphan-list">
        <li v-for="m in orphans" :key="m.badge_uuid">
          <span class="mono">{{ m.badge_uuid }}</span>
          <span class="orphan-name">{{ m.display_name || m.entra_object_id }}</span>
          <button class="link danger" @click="removeOrphan(m)">Remove</button>
        </li>
      </ul>
    </section>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import { getBadgeMappings, setBadgeMapping, deleteBadgeMapping } from '@/services/BadgeService'

const people = ref([])
const mappings = ref([])

// What is in each add field. Separate from the saved cards, so a half-typed
// entry is not treated as a mapping.
const draft = ref({})

// Per row: 'saving', 'saved', or an error message.
const state = ref({})

const loading = ref(true)
const error = ref(null)
const query = ref('')
const onlyUnmapped = ref(false)

// Person id -> every badge they hold. A list, not a single value: people
// carry more than one card.
const savedByPerson = computed(() => {
  const map = {}
  for (const m of mappings.value) {
    (map[m.entra_object_id] ??= []).push(m.badge_uuid)
  }
  for (const list of Object.values(map)) list.sort()
  return map
})

const mappedCount = computed(() =>
  people.value.filter((p) => (savedByPerson.value[p.id] ?? []).length > 0).length
)

const visible = computed(() => {
  const q = query.value.trim().toLowerCase()

  return people.value
    .filter((p) => !onlyUnmapped.value || !(savedByPerson.value[p.id] ?? []).length)
    .filter((p) => {
      if (!q) return true
      return [p.name, p.email].filter(Boolean).some((f) => f.toLowerCase().includes(q))
    })
})

const orphans = computed(() => {
  const known = new Set(people.value.map((p) => p.id))
  return mappings.value.filter((m) => !known.has(m.entra_object_id))
})

async function load() {
  loading.value = true
  error.value = null

  try {
    const [hierarchy, existing] = await Promise.all([
      getSiteHierarchy(),
      getBadgeMappings()
    ])

    mappings.value = existing

    // Everyone, across every country: a badge belongs to a person, not to a
    // place, and filtering by office would hide most of the directory.
    const all = []
    for (const country of hierarchy?.countries ?? []) {
      const inCountry = await getPeopleInCountry(country.code)
      if (inCountry) all.push(...inCountry)
    }

    people.value = all.sort((a, b) => a.name.localeCompare(b.name))

    // The add fields start empty; saved cards are shown as chips.
    draft.value = {}
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

function flash(personId, value) {
  state.value = { ...state.value, [personId]: value }

  if (value === 'saved') {
    setTimeout(() => {
      const next = { ...state.value }
      if (next[personId] === 'saved') delete next[personId]
      state.value = next
    }, 1500)
  }
}

async function commit(person) {
  const typed = (draft.value[person.id] ?? '').trim()
  if (typed === '') return

  const mine = savedByPerson.value[person.id] ?? []
  if (mine.includes(typed)) {
    draft.value = { ...draft.value, [person.id]: '' }
    return
  }

  // The same badge on two people would attribute one person's movements to the
  // other. The backend resolves it by reassigning, which is worse than saying
  // so: the previous owner would silently lose their badge.
  const takenBy = mappings.value.find(
    (m) => m.badge_uuid === typed && m.entra_object_id !== person.id
  )

  if (takenBy) {
    flash(person.id, `already ${takenBy.display_name || 'someone else'}`)
    return
  }

  flash(person.id, 'saving')

  try {
    await setBadgeMapping({
      badgeUuid: typed,
      entraObjectId: person.id,
      displayName: person.name,
      email: person.email ?? ''
    })

    // Updated in place rather than reloading: a full directory fetch after
    // every row would make typing through a list unusable. Added, not
    // replaced: the person's other cards stay.
    mappings.value = [
      ...mappings.value.filter((m) => m.badge_uuid !== typed),
      {
        badge_uuid: typed,
        entra_object_id: person.id,
        display_name: person.name,
        email: person.email ?? ''
      }
    ]

    draft.value = { ...draft.value, [person.id]: '' }
    flash(person.id, 'saved')
  } catch (err) {
    console.error(err)
    flash(person.id, err.message ?? 'failed')
  }
}

async function remove(person, badgeUuid) {
  flash(person.id, 'saving')
  try {
    await deleteBadgeMapping(badgeUuid)
    mappings.value = mappings.value.filter((m) => m.badge_uuid !== badgeUuid)
    flash(person.id, 'saved')
  } catch (err) {
    console.error(err)
    flash(person.id, err.message ?? 'failed')
  }
}

async function removeOrphan(m) {
  try {
    await deleteBadgeMapping(m.badge_uuid)
    mappings.value = mappings.value.filter((x) => x.badge_uuid !== m.badge_uuid)
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  }
}

onMounted(load)
</script>


<style scoped>
.badges {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --ok: #0f8b6c;

  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 18px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 60ch;
}

.counts {
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: var(--ink-soft);
  white-space: nowrap;
}

.counts b {
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  margin-right: 4px;
}

/* ---------- Controls ---------- */

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.search {
  font-family: inherit;
  font-size: 15px;
  height: 44px;
  width: 320px;
  max-width: 100%;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  outline: none;
}

.search:focus { border-color: var(--ink); }

.only-blank {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--ink-soft);
  cursor: pointer;
}

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
  position: sticky;
  top: 0;
  background: #fff;
}

.table td {
  padding: 7px 12px 7px 0;
  border-bottom: 1px solid #f1f4f7;
}

.col-name { width: 24%; }
.col-email { width: 26%; }
.col-badge { width: 38%; }
.col-state { width: 12%; }

.name { font-weight: 600; color: var(--ink); }

.email {
  color: var(--ink-soft);
  font-size: 14px;
}

.badge-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 4px 0 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--wash);
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;
  color: var(--ink);
  max-width: 100%;
}

.chip-id {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.chip-x {
  font-family: inherit;
  font-size: 16px;
  line-height: 1;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: var(--ink-soft);
  cursor: pointer;
  padding: 0;
}

.chip-x:hover { background: #e6ebf0; color: #b3261e; }

.badge-input {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 14px;
  flex: 1 1 140px;
  min-width: 120px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: #fff;
  outline: none;
  box-sizing: border-box;
}

.badge-input:focus { border-color: var(--ink); }

.badge-input::placeholder { color: #c3ccd5; }

/* ---------- Row state ---------- */

.state {
  font-size: 12.5px;
  white-space: nowrap;
}

.pending { color: var(--ink-soft); }
.ok { color: var(--ok); font-weight: 600; }
.bad { color: #b3261e; }

/* ---------- Orphans ---------- */

.orphans {
  margin-top: 34px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin: 0 0 12px;
}

.orphan-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.orphan-list li {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 9px 0;
  border-bottom: 1px solid #f1f4f7;
}

.orphan-name {
  flex: 1;
  color: var(--ink-soft);
  font-size: 14px;
}

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13.5px;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; }

@media (max-width: 760px) {
  .badges { padding: 0 20px 60px; }
  .col-email, .email { display: none; }
  .search { width: 100%; }
}
</style>
